import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Clock } from 'lucide-react';
import { Service } from '../types';

interface ServiceFormProps {
  formData: Service;
  services: Service[];
  isEditing: boolean;
  onFormChange: (data: Service) => void;
  onSubmit: (data: Service) => void;
  onCancel: () => void;
}

export function ServiceForm({ formData: initialData, isEditing, onSubmit, onCancel }: ServiceFormProps) {
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({
    name_he: '',
    price: '',
    duration: ''
  });

  const validateForm = () => {
    const newErrors = {
      name_he: '',
      price: '',
      duration: ''
    };

    if (!formData.name_he) {
      newErrors.name_he = 'שדה חובה';
    }

    if (!formData.price) {
      newErrors.price = 'שדה חובה';
    } else if (isNaN(Number(formData.price)) || Number(formData.price) <= 0) {
      newErrors.price = 'יש להזין מחיר חוקי';
    }

    if (!formData.duration) {
      newErrors.duration = 'שדה חובה';
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSubmit(formData);
  };

  return (
    <div className="relative bg-white border border-gray-300 p-5 rounded-xl shadow-sm">
      <button
        type="button"
        onClick={onCancel}
        className="absolute top-3 left-3 p-1.5 text-gray-600 rounded-lg active:scale-95 transition-transform"
      >
        <X className="h-4 w-4" />
      </button>

      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        {isEditing ? 'עריכת שירות' : 'שירות חדש'}
      </h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            שם השירות <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name_he}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, name_he: e.target.value }));
              if (errors.name_he) setErrors(prev => ({ ...prev, name_he: '' }));
            }}
            className={`w-full px-4 py-2.5 border ${
              errors.name_he ? 'border-red-300 bg-red-50/50' : 'border-gray-300 bg-white'
            } rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none transition-all text-right text-sm`}
            placeholder="לדוגמה: תספורת רגילה"
          />
          {errors.name_he && (
            <p className="mt-1.5 text-[13px] text-red-600">
              {errors.name_he}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              מחיר <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="number"
                value={formData.price}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, price: e.target.value }));
                  if (errors.price) setErrors(prev => ({ ...prev, price: '' }));
                }}
                className={`w-full px-4 py-2.5 pr-8 border ${
                  errors.price ? 'border-red-300 bg-red-50/50' : 'border-gray-300 bg-white'
                } rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none transition-all text-right text-sm`}
                placeholder="0"
                min="0"
                step="0.01"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">₪</span>
            </div>
            {errors.price && (
              <p className="mt-1.5 text-[13px] text-red-600">
                {errors.price}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              משך זמן <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.duration}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, duration: e.target.value }));
                if (errors.duration) setErrors(prev => ({ ...prev, duration: '' }));
              }}
              className={`w-full px-4 py-2.5 border ${
                errors.duration ? 'border-red-300 bg-red-50/50' : 'border-gray-300 bg-white'
              } rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none transition-all text-right appearance-none text-sm`}
            >
              <option value="15">15 דקות</option>
              <option value="30">30 דקות</option>
              <option value="45">45 דקות</option>
              <option value="60">שעה</option>
              <option value="90">שעה וחצי</option>
              <option value="120">שעתיים</option>
            </select>
            {errors.duration && (
              <p className="mt-1.5 text-[13px] text-red-600">
                {errors.duration}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-2 mt-5">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 text-gray-700 bg-gray-100 rounded-lg font-medium hover:bg-gray-200 active:scale-95 transition-all text-sm"
        >
          ביטול
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 active:scale-95 transition-all text-sm"
        >
          {isEditing ? 'עדכן' : 'הוסף'}
        </button>
      </div>
    </div>
  );
}
